import React, { Fragment } from 'react';
import { NavLink } from 'react-router-dom';
import { Icon } from '@material-ui/core';
import TouchRipple from '@material-ui/core/ButtonBase';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import useSettings from 'hooks/useSettings';
import VerticalNavExpansionPanel from './VerticalNavExpansionPanel';


const useStyles = makeStyles(({ palette }) => ({
  navItem: {
    transition: 'all 150ms ease-in',

    '&:hover': {
      backgroundColor: 'rgb(44, 74, 161)',
    },
  },
  navItemActive: {
    backgroundColor: palette.action.selected,
  },
  compactNavItem: {
    overflow: 'hidden',
    justifyContent: 'center !important',

    '& $itemText': {
      display: 'none',
    },

    '& $itemIcon': {
      display: 'none',
    },
  },
  backgroundPrimary: {
    backgroundColor: '#103A99',
  },
  itemIcon: {},

  itemText: {
    fontSize: '0.875rem',
    paddingLeft: '0.8rem',
    lineHeight: '1.64',
  },

  positionBottom: {
    bottom: '90px !important'
  },

  positionBottomHelp: {
    bottom: '44px !important'
  },

  label: {
    color: palette.text.secondary,
  },

  bulletIcon: {
    background: palette.text.secondary,
  },
}));

const VerticalNav = ({ items }) => {
  const { settings } = useSettings();
  const { mode } = settings.layout1Settings.leftSidebar;
  const classes = useStyles();

  const renderLevels = (data) => {
    return data.map((item, index) => {
      if (item.isActive && item.isPermission) {
        if (item.type === 'label') {
          return (
            <p
              key={`item-${index}`}
              className={clsx({
                'px-4 mb-2 mt-6 uppercase text-12 sidenavHoverShow': true,
                [classes.label]: true,
                hidden: mode === 'compact',
              })}
            >
              {item.label}
            </p>
          );
        }

        if (item.children) {
          return (
            <VerticalNavExpansionPanel
              mode={mode}
              item={item}
              key={`panel-${index}`}
            >
              {renderLevels(item.children)}
            </VerticalNavExpansionPanel>
          );
        } else if (item.type === 'extLink') {
          const path = item.target ? {pathname: item.path} : item.path;
          return (
            <NavLink
              key={`ext-link-${index}`}
              target={item.target}
              to={path}
              className={clsx({
                'flex justify-between h-44 border-radius-4 mb-2 compactNavItem overflow-hidden absolute position-bottom w-full max-w-228 mr-4': true,
                [classes.navItem]: true,
                [classes.positionBottom]: item.name === 'Settings',
                [classes.compactNavItem]: mode === 'compact',
                [classes.positionBottomHelp]: item.name === 'Help',
              })}
              title={item.title}
              activeClassName={classes.navItemActive}
            >
              <TouchRipple
                key={`name-${item.name}`}
                name="child"
                className="w-full"
              >
                {(() => {
                  if (item.icon) {
                    return (
                      <Icon className="text-18 align-middle w-36 px-4">
                        {item.icon}
                      </Icon>
                    );
                  }

                  return (
                    <span className="item-icon icon-text">
                      {item.iconText}
                    </span>
                  );
                })()}
                <span
                  className={clsx(
                    'align-middle sidenavHoverShow',
                    classes.itemText
                  )}
                >
                  {item.name}
                </span>
                <div className="mx-auto" />
                {item.badge && (
                  <div
                    className={`rounded bg-${item.badge.color} px-1 py-1px`}
                  >
                    {item.badge.value}
                  </div>
                )}
              </TouchRipple>
            </NavLink>
          );
        }
      }

      return (
        <div key={index}>
          {item.isActive && item.isPermission &&
          <NavLink
            exact
            key={`nav-link-${index}`}
            to={item.path}
            activeClassName={classes.navItemActive}
            title={item.title}
            className={clsx({
              'flex justify-between h-44 border-radius-4 mb-2 compactNavItem overflow-hidden': true,
              [classes.navItem]: true,
              [classes.compactNavItem]: mode === 'compact',
            })}
          >
            <TouchRipple
              key={`nav-link-${item.name}`}
              name="child"
              className="w-full"
            >
              {item?.icon ? (
                <Icon className="text-18 align-middle w-36 px-4">
                  {item.icon}
                </Icon>
              ) : (
                <Fragment>
                  <div
                    className={clsx({
                      'nav-bullet p-2px rounded bg-default ml-6 mr-2': true,
                      [classes.itemText]: true
                    })}
                  />
                  <div
                    className={clsx({
                      'nav-bullet-text ml-5 text-11': true,
                      hidden: mode !== 'compact',
                    })}
                  >
                    {item.iconText}
                  </div>
                </Fragment>
              )}
              <span
                className={clsx(
                  'align-middle text-left sidenavHoverShow',
                  classes.itemText
                )}
              >
                {item.name}
              </span>
              <div className="mx-auto" />
              {item.badge && (
                <div
                  className={clsx(
                    `rounded bg-${item.badge.color} px-1 py-1px`,
                    'sidenavHoverShow',
                    classes.itemIcon
                  )}
                >
                  {item.badge.value}
                </div>
              )}
            </TouchRipple>
          </NavLink>
          }
        </div>
      );
    });
  };

  return <div className="navigation h-full w-full">{renderLevels(items)}</div>;
};

export default React.memo(VerticalNav);
